import { Merchant } from 'apis/restaurant-api/merchant-admin/merchant/types';
import { UserAccessRight } from 'apis/restaurant-api/merchant-admin/user/types';
import { PosIntegrationStatus, Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { hasPermissions } from 'common/wolt-permissions';
import type { AccessDescription, EntityType, Role } from 'modules/access-control';

const getRoles = (accessRights: UserAccessRight[], type: EntityType, entityId: string): Role[] => {
  return accessRights.filter(access => access.type === type && access.id === entityId).map(access => access.role);
};

export const checkRole = (
  accessRights: UserAccessRight[],
  type: EntityType,
  entityId: string,
  check: (roles: Role[]) => boolean,
) => {
  const roles = getRoles(accessRights, type, entityId);
  return check(roles);
};

export const userAccessRoles: Role[] = [
  'merchant_owner',
  'venue_worker',
  'venue_manager',
  'venue_manager_lite',
  'venue_finance_manager',
  'venue_group_owner',
  'restricted_menu_editor',
];

export const featuresToRolesMap: { [key: string]: Role[] } = {
  venueInsights: [
    'merchant_owner',
    'venue_manager',
    'venue_manager_lite',
    'venue_finance_manager',
    'venue_group_owner',
  ],
  menuEditor: [
    'venue_worker',
    'venue_manager',
    'venue_manager_lite',
    'venue_finance_manager',
    'restricted_menu_editor',
  ],
  menuCreate: ['venue_manager', 'venue_manager_lite', 'venue_finance_manager', 'restricted_menu_editor'],
  menuItemVATChange: ['venue_finance_manager', 'restricted_menu_editor'],
  menuContentEdit: [
    'venue_worker',
    'venue_manager',
    'venue_manager_lite',
    'venue_finance_manager',
    'restricted_menu_editor',
  ],
  menuPosId: ['venue_manager', 'venue_manager_lite', 'venue_finance_manager', 'restricted_menu_editor'],
  venueDetailsView: ['venue_manager', 'venue_manager_lite', 'venue_finance_manager'],
  venueDetailsEdit: ['venue_manager', 'venue_manager_lite', 'venue_finance_manager'],
  purchases: ['venue_manager', 'venue_manager_lite', 'venue_finance_manager'],
  settlements: ['venue_manager', 'venue_manager_lite', 'venue_finance_manager'],
  payoutReports: ['venue_manager', 'venue_manager_lite', 'venue_finance_manager'],
  accessManagement: ['venue_manager', 'venue_manager_lite', 'venue_finance_manager'],
  translation: [
    'venue_worker',
    'venue_manager',
    'venue_manager_lite',
    'venue_finance_manager',
    'restricted_menu_editor',
  ],
  appPasswords: ['venue_worker', 'venue_manager'],
  appPasswordsCreateDelete: ['venue_manager'],
  campaignsView: ['venue_worker', 'venue_manager', 'venue_manager_lite', 'venue_finance_manager', 'merchant_owner'],
  campaignsEdit: ['merchant_owner', 'venue_finance_manager', 'venue_manager'],
  deliveryProviderEdit: ['venue_manager'],
};

export const hasVenueDetailsViewPermission = (permissions: string[], accessRights: UserAccessRight[], venue: Venue) => {
  if (hasPermissions([{ resource: 'restaurant', operations: ['r'] }], permissions)) {
    return true;
  }
  return checkRole(accessRights, 'venue', venue.id, roles =>
    roles.some(role => featuresToRolesMap.venueDetailsView.includes(role)),
  );
};

export const hasVenueDetailsEditPermissions = (
  permissions: string[],
  accessRights: UserAccessRight[],
  venue: Venue,
) => {
  if (hasPermissions([{ resource: 'restaurant', operations: ['r', 'u'] }], permissions)) {
    return true;
  }
  return checkRole(accessRights, 'venue', venue.id, roles =>
    roles.some(role => featuresToRolesMap.venueDetailsEdit.includes(role)),
  );
};

export const hasVenueVisualSettingsEditPermission = (permissions: string[]) => {
  return hasPermissions([{ resource: 'restaurant', operations: ['r', 'u'] }], permissions);
};

export const hasVenueInsightsPermission = (permissions: string[], accessRights: UserAccessRight[], venue: Venue) => {
  return (
    hasPermissions([{ resource: 'restaurant', operations: ['r'] }], permissions) ||
    checkRole(accessRights, 'venue', venue.id, roles =>
      roles.some(role => featuresToRolesMap.venueInsights.includes(role)),
    )
  );
};

export const hasMenuEditorPermission = (permissions: string[], accessRights: UserAccessRight[], venue: Venue) => {
  return (
    hasPermissions([{ resource: 'restaurant', operations: ['r'] }], permissions) ||
    checkRole(accessRights, 'venue', venue.id, roles =>
      roles.some(role => featuresToRolesMap.menuEditor.includes(role)),
    )
  );
};

export const hasPickingRoutePermission = (permissions: string[], accessRights: UserAccessRight[], venue: Venue) => {
  return (
    hasPermissions([{ resource: 'restaurant', operations: ['r'] }], permissions) ||
    checkRole(accessRights, 'venue', venue.id, roles =>
      roles.some(role => featuresToRolesMap.menuEditor.includes(role)),
    )
  );
};

export const hasCreateMenuPermission = (permissions: string[], accessRights: UserAccessRight[], venue: Venue) => {
  return (
    hasPermissions([{ resource: 'restaurant', operations: ['c'] }], permissions) ||
    checkRole(accessRights, 'venue', venue.id, roles =>
      roles.some(role => featuresToRolesMap.menuCreate.includes(role)),
    )
  );
};

// This permission is not enforced by the backend
export const hasSystemIdReadPermission = (permissions: string[], accessRights: UserAccessRight[], venue: Venue) => {
  return (
    hasPermissions([{ resource: 'restaurant', operations: ['r'] }], permissions) ||
    checkRole(accessRights, 'venue', venue.id, roles => roles.includes('restricted_menu_editor'))
  );
};

export const hasPosIdFieldPermission = (permissions: string[], accessRights: UserAccessRight[], venue: Venue) => {
  return (
    hasPermissions([{ resource: 'restaurant', operations: ['r', 'u'] }], permissions) ||
    checkRole(accessRights, 'venue', venue.id, roles => roles.some(role => featuresToRolesMap.menuPosId.includes(role)))
  );
};

export const hasMenuLanguageChangePermission = (permissions: string[]) => {
  return hasPermissions([{ resource: 'restaurant', operations: ['u'] }], permissions);
};

export const hasWoltSpecialsViewPermission = (permissions: string[]) => {
  return hasPermissions([{ resource: 'restaurant', operations: ['r'] }], permissions);
};

// hasMenuContentEditPermission exists for two reasons:
//
// * We want to prevent most users from editing the content of a menu that is POS integrated (so the menu content comes
//   from an external system)
// * In Germany, Wolt is legally responsible for menu content and to avoid lawsuits stemming from incorrect menu
//   content, we don't want to allow merchants to edit it.
//
// This permission is an ad-hoc design and its scope is arbitrary and overly big. In the future, this should be replaced
// with something that is more carefully thought out.
export const hasMenuContentEditPermission = (
  permissions: string[],
  accessRights: UserAccessRight[],
  venue: Venue,
  venuePosIntegrationStatus?: PosIntegrationStatus | null,
) => {
  // we don't have the integration status probably because of error, so we prevent editing...
  if (!venuePosIntegrationStatus) {
    return false;
  }

  // we restrict menu editing if venue is menu integrated restaurant, except for Japan, due to woflow issue
  if (venue.product_line === 'restaurant' && venuePosIntegrationStatus.is_menu_integrated && venue.country !== 'JPN') {
    const hasValidInternalPermissions = hasPermissions(
      [{ resource: 'restaurant.pos', operations: ['u'] }],
      permissions,
    );
    const hasValidExternalRole = checkRole(accessRights, 'venue', venue.id, roles =>
      roles.includes('restricted_menu_editor'),
    );
    return hasValidInternalPermissions || hasValidExternalRole;
  }

  if (hasPermissions([{ resource: 'restaurant', operations: ['u'] }], permissions)) {
    return true;
  }

  return checkRole(accessRights, 'venue', venue.id, roles =>
    roles.some(role => featuresToRolesMap.menuContentEdit.includes(role)),
  );
};

export const hasMenuExportImportPermission = (
  permissions: string[],
  accessRights: UserAccessRight[],
  venue: Venue,
  venuePosIntegrationStatus?: PosIntegrationStatus | null,
) => {
  return (
    hasMenuContentEditPermission(permissions, accessRights, venue, venuePosIntegrationStatus) &&
    (hasPermissions([{ resource: 'restaurant', operations: ['r', 'c'] }], permissions) ||
      checkRole(
        accessRights,
        'venue',
        venue.id,
        roles =>
          roles.includes('venue_manager') ||
          roles.includes('venue_finance_manager') ||
          roles.includes('restricted_menu_editor'),
      ))
  );
};

export const hasMenuSyncPermission = (
  permissions: string[],
  accessRights: UserAccessRight[],
  venue: Venue,
  venuePosIntegrationStatus?: PosIntegrationStatus | null,
) => {
  return (
    hasMenuContentEditPermission(permissions, accessRights, venue, venuePosIntegrationStatus) &&
    hasPermissions([{ resource: 'restaurant', operations: ['r', 'c'] }], permissions)
  );
};

export const hasMenuItemExportImportPermission = (
  permissions: string[],
  accessRights: UserAccessRight[],
  venue: Venue,
) => {
  return (
    hasPermissions([{ resource: 'restaurant', operations: ['r', 'c'] }], permissions) ||
    checkRole(accessRights, 'venue', venue.id, roles => roles.includes('restricted_menu_editor'))
  );
};

export const hasMenuItemVATChangePermission = (
  permissions: string[],
  accessRights: UserAccessRight[],
  venue: Venue,
) => {
  return (
    hasPermissions([{ resource: 'restaurant', operations: ['u'] }], permissions) ||
    checkRole(accessRights, 'venue', venue.id, roles =>
      roles.some(role => featuresToRolesMap.menuItemVATChange.includes(role)),
    )
  );
};

export const hasMenuDiscountPermission = (permissions: string[]) => {
  return hasPermissions([{ resource: 'restaurant', operations: ['u'] }], permissions);
};

export const hasPurchasesPermission = (permissions: string[], accessRights: UserAccessRight[], venue: Venue) => {
  return (
    hasPermissions([{ resource: 'purchase', operations: ['r'] }], permissions) ||
    checkRole(accessRights, 'venue', venue.id, roles => roles.some(role => featuresToRolesMap.purchases.includes(role)))
  );
};

export const hasSettlementsPermission = (
  permissions: string[],
  externalAccessRights: UserAccessRight[],
  venue: Venue,
) => {
  if (hasPermissions([{ resource: 'settlement', operations: ['r'] }], permissions)) {
    return true;
  }
  return checkRole(externalAccessRights, 'venue', venue.id, roles =>
    roles.some(role => featuresToRolesMap.settlements.includes(role)),
  );
};

export const hasPayoutReportsPermission = (permissions: string[], accessRights: UserAccessRight[], venue: Venue) => {
  return (
    hasPermissions([{ resource: 'restaurant', operations: ['c', 'r'] }], permissions) ||
    checkRole(accessRights, 'venue', venue.id, roles =>
      roles.some(role => featuresToRolesMap.payoutReports.includes(role)),
    )
  );
};

export const hasVenueUserManagementPermission = (
  permissions: string[],
  accessRights: UserAccessRight[],
  venue: Venue,
) => {
  return (
    hasPermissions([{ resource: 'restaurant', operations: ['r', 'u', 'd'] }], permissions) ||
    checkRole(accessRights, 'venue', venue.id, roles =>
      roles.some(role => featuresToRolesMap.accessManagement.includes(role)),
    )
  );
};

export const hasTranslationPermission = (permissions: string[], accessRights: UserAccessRight[], venue: Venue) => {
  if (hasPermissions([{ resource: 'restaurant', operations: ['u'] }], permissions)) {
    return true;
  }
  // Danish team doesn't want Translation tab visible to merchants
  if (venue && venue.country.includes('DNK')) {
    return false;
  }
  return checkRole(accessRights, 'venue', venue.id, roles =>
    roles.some(role => featuresToRolesMap.translation.includes(role)),
  );
};

export const hasMenuCustomCommissionEditPermission = (permissions: string[]) => {
  return hasPermissions([{ resource: 'merchant.finance', operations: ['u'] }], permissions);
};

export const hasMenuCustomCommissionReadPermission = (permissions: string[]) => {
  return hasPermissions([{ resource: 'restaurant', operations: ['r'] }], permissions);
};

export const hasCatalogCreatePermission = (permissions: string[]) => {
  return hasPermissions([{ resource: 'catalog.products', operations: ['c', 'r', 'u', 'd'] }], permissions);
};

export const hasCatalogReadPermission = (permissions: string[]) => {
  return hasPermissions([{ resource: 'catalog.products', operations: ['r'] }], permissions);
};

export const hasMenuCategoryImagesPermission = (
  permissions: string[],
  accessRights: UserAccessRight[],
  venue: Venue,
) => {
  return (
    hasPermissions([{ resource: 'restaurant', operations: ['r', 'u'] }], permissions) ||
    checkRole(accessRights, 'venue', venue.id, roles => roles.includes('restricted_menu_editor'))
  );
};

export const hasCsvItemPriceUpdatePermission = (permissions: string[]) => {
  return hasPermissions([{ resource: 'restaurant', operations: ['u'] }], permissions);
};

export const hasCsvItemUpdatePermission = (permissions: string[], accessRights: UserAccessRight[], venue: Venue) => {
  return (
    hasPermissions([{ resource: 'restaurant', operations: ['u'] }], permissions) ||
    checkRole(accessRights, 'venue', venue.id, roles => roles.includes('restricted_menu_editor'))
  );
};

export const hasCsvItemImportPermission = (permissions: string[], accessRights: UserAccessRight[], venue: Venue) => {
  return (
    hasPermissions([{ resource: 'restaurant', operations: ['c'] }], permissions) ||
    checkRole(accessRights, 'venue', venue.id, roles => roles.includes('restricted_menu_editor'))
  );
};

// User search /v1/merchant-admin/users requires user:r permission
export const hasDirectAddUsersPermission = (permissions: string[]) => {
  return hasPermissions([{ resource: 'user', operations: ['r'] }], permissions);
};

const hasEntityDeletePermission = (entityType: EntityType, permissions: string[]): boolean => {
  switch (entityType) {
    // Enterprises and Brands are secured behind the `merchant` resource
    case 'enterprise':
    case 'brand':
    case 'merchant':
      return hasPermissions([{ resource: 'merchant', operations: ['d'] }], permissions);
    case 'venue':
      return hasPermissions([{ resource: 'restaurant', operations: ['d'] }], permissions);
    default:
      return false;
  }
};

const canDeleteEnterpriseUsers = (entityType: EntityType, roles: Role[]) =>
  entityType === 'enterprise' && roles.includes('enterprise_owner');

const canDeleteBrandUsers = (entityType: EntityType, roles: Role[]) =>
  entityType === 'brand' && roles.includes('brand_owner');

const canDeleteMerchantUsers = (entityType: EntityType, roles: Role[]) =>
  entityType === 'merchant' && roles.includes('merchant_owner');

// Venue managers can only delete workers
const canDeleteVenueUsers = (entityType: EntityType, roles: Role[], userRole: string) =>
  entityType === 'venue' && roles.includes('venue_manager') && userRole === 'venue_worker';

export const hasAccessRemovalPermission = (
  currentUserEmail: string,
  targetUser: AccessDescription,
  entityType: EntityType,
  entityId: string,
  permissions: string[],
  accessRights: UserAccessRight[],
) => {
  const isDeletingOwnUser = currentUserEmail === targetUser.email;
  const hasDeletePermissions = hasEntityDeletePermission(entityType, permissions);
  const hasDeleteAccessRights = checkRole(
    accessRights,
    entityType,
    entityId,
    roles =>
      canDeleteEnterpriseUsers(entityType, roles) ||
      canDeleteBrandUsers(entityType, roles) ||
      canDeleteMerchantUsers(entityType, roles) ||
      canDeleteVenueUsers(entityType, roles, targetUser.role),
  );

  // Can't delete your own rights
  if (isDeletingOwnUser) {
    return false;
  }

  return hasDeletePermissions || hasDeleteAccessRights;
};

// /v1/merchant-admin/merchants/:merchant_id/menu-api-users endpoints require merchant:crud permissions
// /v1/merchant-admin/merchants/:merchant_id/order-api/credentials endpoints require merchant:crud permissions
export const hasApiManagementPermission = (
  permissions: string[],
  accessRights: UserAccessRight[],
  merchant: Merchant,
) => {
  return (
    hasPermissions([{ resource: 'merchant', operations: ['c', 'r', 'u', 'd'] }], permissions) ||
    checkRole(accessRights, 'merchant', merchant.itemid.$oid, roles => roles.includes('merchant_owner'))
  );
};

// POST and GET on /venues/:venue_id/api-tokens require restaurant.pos:cr
export const hasApiKeyGenerationPermission = (permissions: string[]) => {
  return hasPermissions([{ resource: 'restaurant.pos', operations: ['c', 'r'] }], permissions);
};

export const hasMerchantAppLoginPermissions = (
  permissions: string[],
  accessRights: UserAccessRight[],
  venue: Venue,
) => {
  return (
    hasPermissions([{ resource: 'restaurant', operations: ['u'] }], permissions) ||
    checkRole(accessRights, 'venue', venue.id, roles =>
      roles.some(role => featuresToRolesMap.appPasswords.includes(role)),
    )
  );
};

export const hasMerchantAppCreateDeletePermissions = (
  permissions: string[],
  accessRights: UserAccessRight[],
  venue: Venue,
) => {
  return (
    hasPermissions([{ resource: 'restaurant', operations: ['u'] }], permissions) ||
    checkRole(accessRights, 'venue', venue.id, roles =>
      roles.some(role => featuresToRolesMap.appPasswordsCreateDelete.includes(role)),
    )
  );
};

export const hasCampaignViewPermission = (permissions: string[], accessRights: UserAccessRight[], venue: Venue) => {
  return (
    hasPermissions([{ resource: 'restaurant', operations: ['r'] }], permissions) ||
    checkRole(accessRights, 'venue', venue.id, roles =>
      roles.some(role => featuresToRolesMap.campaignsView.includes(role)),
    )
  );
};

export const hasCampaignEditPermission = (
  permissions: string[],
  accessRights: UserAccessRight[],
  entityType: EntityType,
  entityId: string,
) => {
  return (
    hasPermissions([{ resource: 'restaurant', operations: ['c', 'd'] }], permissions) ||
    checkRole(accessRights, entityType, entityId, roles =>
      roles.some(role => featuresToRolesMap.campaignsEdit.includes(role)),
    )
  );
};

export const hasDeliveryProviderEditPermissions = (
  permissions: string[],
  accessRights: UserAccessRight[],
  venue: Venue,
) => {
  return (
    hasPermissions([{ resource: 'restaurant', operations: ['u'] }], permissions) ||
    checkRole(accessRights, 'venue', venue.id, roles =>
      roles.some(role => featuresToRolesMap.deliveryProviderEdit.includes(role)),
    )
  );
};
